// Routes the EventPlayer's bonus hooks to the view for the bonus that is running.
import type { Ticker } from 'pixi.js';
import type { BonusMode } from '../../sim/types';
import { getGame } from '../../state/store';
import type { Fx } from '../fx/fx';
import type { Popups } from '../fx/Popups';
import { FeastView } from './FeastView';
import { HiloView } from './HiloView';
import { PlinkoView } from './PlinkoView';
import { ScratchView } from './ScratchView';
import { WheelView } from './WheelView';

interface BonusView {
  visible: boolean;
  open(): void;
  update(): void | Promise<void>;
  close(payout: string, voided: boolean): Promise<void>;
}

export class BonusViews {
  readonly scratch: ScratchView;
  readonly hilo: HiloView;
  readonly wheel: WheelView;
  readonly plinko: PlinkoView;
  readonly feast: FeastView;
  private current: BonusView | null = null;

  constructor(popups: Popups, fx: Fx, ticker: Ticker) {
    this.scratch = new ScratchView(popups);
    this.hilo = new HiloView(popups, fx);
    this.wheel = new WheelView(popups);
    this.wheel.bindTicker(ticker);
    this.plinko = new PlinkoView(popups, fx);
    this.feast = new FeastView(popups, fx);
  }

  get layers() {
    return [this.scratch, this.hilo, this.wheel, this.plinko, this.feast];
  }

  private viewFor(mode: BonusMode | undefined): BonusView | null {
    if (mode === 'scratch') return this.scratch;
    if (mode === 'hilo') return this.hilo;
    if (mode === 'wheel') return this.wheel;
    if (mode === 'plinko') return this.plinko;
    if (mode === 'feast') return this.feast;
    return null;
  }

  open(): void {
    this.current = this.viewFor(getGame().run?.bonus?.mode);
    this.current?.open();
  }

  async update(): Promise<void> {
    await this.current?.update();
  }

  async close(payout: string, voided: boolean): Promise<void> {
    await this.current?.close(payout, voided);
    this.current = null;
  }

  /** After a reload or screen change: show the view for the running bonus, hide the others. */
  sync(show: boolean): void {
    const mode = getGame().run?.bonus?.mode;
    for (const v of this.layers) if (!show || this.viewFor(mode) !== v) v.visible = false;
  }
}
